import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SSE } from 'sse.js';
import ErrorMessage from '../../errorMessage';
import PipelineBuilder from '../../pipelineBuilder/pipelineBuilder';
import MissingSettingsPanel from '../MissingSettingsPanel';

type Props = {
  surveyId: string;
  userResponses: Record<string, any>;
  maxHeight?: number;
  onSurveyResponseId: (surveyResponseId: string) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepProcessing = ({ surveyId, userResponses, onSurveyResponseId, onBack, onCancel, onNext }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();

  const [streamSource, setStreamSource] = useState<SSE>();
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [tryAgain, setIsTryAgain] = useState(true);
  const [surveyResponseId, setSurveyResponseId] = useState<string>('');
  const [isCreatingPipeline, setIsCreatingPipeline] = useState(false);

  // Tracks edited settings from MissingSettingsPanel keyed by item index
  const [itemSettings, setItemSettings] = useState<Record<number, Record<string, any>>>({});

  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const submitUserSurvey = async () => {
      try {
        const source = await apiContext.submitUserSurvey(
          surveyId,
          { surveyId, responses: userResponses },
          (eventName, payLoad) => {
            const { message, isComplete, errorCode } = payLoad || {};
            if (eventName === 'error') {
              setError({ message, errorCode });
              if (message) {
                setStatusMessages((prev) => (prev.includes(message) ? prev : [...prev, message]));
              }
            } else if (isComplete) {
              setSurveyResponseId(message);
              onSurveyResponseId(message);
            } else {
              setStatusMessages((prevValue) => [...prevValue, message]);
            }
            setIsComplete((prevValue) => isComplete);
          },
        );
        setStreamSource(source);
      } catch (error: any) {
        setError(error);
        if (error?.message) {
          setStatusMessages((prev) => (prev.includes(error.message) ? prev : [...prev, error.message]));
        }
      }
      setIsTryAgain(false);
    };

    if (userResponses && tryAgain) {
      setError(null);
      setStatusMessages([]);
      submitUserSurvey();
    }
  }, [surveyId, userResponses, tryAgain]);

  const handleCancel = () => {
    if (streamSource) {
      streamSource.close();
      setStreamSource(undefined);
    }
    onCancel();
  };

  const handleSettingsChange = (itemIndex: number, _entityDefId: string, updatedSettings: Record<string, any>) => {
    setItemSettings((prev) => ({ ...prev, [itemIndex]: updatedSettings }));
  };

  const handleCreateUserSurveyPipeline = async () => {
    try {
      if (streamSource) {
        streamSource.close();
        setStreamSource(undefined);
      }
      setError(null);
      setIsCreatingPipeline(true);

      // Pass any inline-edited settings directly to create
      const updates = Object.entries(itemSettings)
        .filter(([, settings]) => Object.keys(settings).length > 0)
        .map(([idx, settings]) => ({ itemIndex: parseInt(idx), settings }));

      await apiContext.createUserSurveyPipeline(surveyId, surveyResponseId, updates.length > 0 ? updates : undefined);
      onNext();
    } catch (error: any) {
      setError(error);
    } finally {
      setIsCreatingPipeline(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        p: 2,
      }}
      ref={containerRef}
    >
      {/* Scrollable content area */}
      <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
        <Stack spacing={2}>
          {error && <ErrorMessage error={error} />}

          {/* Processing state */}
          {!error && !isComplete && (
            <>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={18} color="secondary" thickness={4} />
                <Typography sx={{ fontWeight: 600 }}>
                  Analyzing your requirements and designing your pipeline…
                </Typography>
              </Stack>
              <LinearProgress color="secondary" sx={{ borderRadius: '4px' }} />
            </>
          )}

          {/* Status log */}
          {statusMessages.length > 0 && (
            <Box
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.7),
                border: 1,
                borderColor: error ? 'error.light' : 'divider',
                borderRadius: '8px',
                p: 1.5,
                maxHeight: 140,
                overflowY: 'auto',
              }}
            >
              <Stack spacing={0.5}>
                {statusMessages.map((message, idx) => {
                  const isLast = idx === statusMessages.length - 1;
                  let icon = <CheckCircleIcon sx={{ fontSize: 11, color: 'success.main', mt: '3px', flexShrink: 0 }} />;

                  if (error && isLast) {
                    icon = <ErrorIcon sx={{ fontSize: 11, color: 'error.main', mt: '3px', flexShrink: 0 }} />;
                  } else if (isLast && !isComplete) {
                    icon = <CircularProgress size={10} color="secondary" sx={{ mt: '3px', flexShrink: 0 }} />;
                  }

                  return (
                    <Stack key={idx} direction="row" spacing={0.75} alignItems="flex-start">
                      {icon}
                      <Typography
                        variant="caption"
                        color={error && isLast ? 'error.main' : 'text.secondary'}
                        sx={{ fontWeight: error && isLast ? 600 : 400 }}
                      >
                        {message}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Complete state — pipeline preview */}
          {!error && isComplete && (
            <>
              <Alert severity="success" sx={{ borderRadius: '10px', py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Pipeline design ready! Review below, then click <strong>Create pipeline</strong>.
                </Typography>
              </Alert>
              <Box sx={{ height: '260px' }}>
                <PipelineBuilder
                  maxHeight={containerSize.height}
                  entity={undefined}
                  surveyId={surveyId}
                  responseId={surveyResponseId}
                  viewOnly={true}
                  onConfigurationChange={() => {}}
                  isOpen={true}
                />
              </Box>

              <Divider />

              {/* Missing settings panel */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Required Settings
                </Typography>
                <MissingSettingsPanel
                  surveyId={surveyId}
                  surveyResponseId={surveyResponseId}
                  onSettingsChange={handleSettingsChange}
                  itemSettings={itemSettings}
                />
              </Box>
            </>
          )}
        </Stack>
      </Box>

      {/* Action buttons pinned to the bottom */}
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent={'flex-end'}
        sx={{ width: '100%', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button
          variant="text"
          onClick={handleCancel}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          {t('common.buttons.cancel')}
        </Button>
        {error && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setIsTryAgain(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 2.5,
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {t('Try again')}
          </Button>
        )}
        {isComplete && !error && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreateUserSurveyPipeline}
            disabled={isCreatingPipeline}
            startIcon={isCreatingPipeline ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 2.5,
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {isCreatingPipeline ? 'Creating…' : t('Create pipeline')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default StepProcessing;
